import React, { useState } from 'react'
import classnames from 'classnames'
import Flag from 'react-flagkit'
import ActionDropdown from '@SiteComponents/ActionDropdown'
import getLanguageStatusForLanguagesView from '@Utils/GetLanguageStatusForLanguagesView'
import getAllowLinkToEditor from '@Utils/GetAllowLinkToEditor'
import WorldFlag from '@Images/world_flag.svg'
import More from '@Images/projects_table_list_more.svg'
import Done from '@Images/languages_view_done.svg'
import InProgress from '@Images/languages_view_in_progress.svg'
import Pending from '@Images/languages_view_pending.svg'

import {
  IOpenImportSrtPopupPayload,
  IOpenExternalTokenPopupPayload,
} from '@SiteModules/Popup/types'

import {
  ISubtitle,
  IDeleteLanguagePayload,
} from '@SiteModules/Subtitle/types'

import './LanguageRow.scss'

interface LanguageRowProps {
  readonly index: number
  readonly isDisabled: boolean
  readonly subtitle: ISubtitle
  readonly projectVersionIsShortened: boolean
  handleDeleteLanguage(payload: IDeleteLanguagePayload): void
  handleOpenImportSrtPopup(payload: IOpenImportSrtPopupPayload): void
  handleOpenExternalTokenPopup(payload: IOpenExternalTokenPopupPayload): void
}

const SUBTITLE_TYPE = 'Subtitle'
const TTS_TYPE = 'Text-to-speech'
const SUBTITLE_AND_DUBBING_TYPE = 'Subtitle, Dubbing'

const LanguageRow: React.FC<LanguageRowProps> = props => {
  const {
    index,
    subtitle,
    isDisabled,
    projectVersionIsShortened,
  } = props

  const {
    language,
    hasVoice,
    belongsToTtsProject,
    projectId,
    projectVersionId,
    stage,
    countryFlag,
    transcription,
    percentageReviewed,
  } = subtitle.attributes

  const [showDropdown, setShowDropdown] = useState(false)

  const [languageStatusClass, languageStatusToRender] = getLanguageStatusForLanguagesView(stage)
  const isDone = languageStatusClass === 'done'
  const isPendingTranscriptOrInsufficientCreditsOrWaitingReview = ['pending_transcript', 'insufficient_credits', 'waiting_review'].includes(languageStatusClass)
  const isInProgress = languageStatusClass === 'in_progress'
  const isWaitingReview = languageStatusClass === 'waiting_review'
  const isCountryFlagAvailable = !!countryFlag
  const shouldAllowToImportSrt = !belongsToTtsProject && !projectVersionIsShortened && (isDone || isWaitingReview)
  const shouldAllowToShareLanguage = isDone || isWaitingReview
  const shouldAllowToDeleteLanguage = !transcription
  const hasDropdownOptions = shouldAllowToImportSrt || shouldAllowToShareLanguage || shouldAllowToDeleteLanguage
  const cleanLanguage = language.replace(/[^a-zA-Z]/g, '-').replace(/^-+|-+$/g, '').replace(/-+/g, '-')
  const moreElementId = `language_row_more_${cleanLanguage}`
  const classNameForClickOutsideListenerMoreDropdown = `LanguagesTable__action-dropdown-element-${cleanLanguage}`

  let languageType = SUBTITLE_TYPE
  if (belongsToTtsProject) {
    languageType = TTS_TYPE
  } else if (hasVoice) {
    languageType = SUBTITLE_AND_DUBBING_TYPE
  }

  const allowLinkToEditor = getAllowLinkToEditor(isDisabled, subtitle.attributes)

  const openEditor = () => {
    if (!allowLinkToEditor) { return }

    const urlParams = hasVoice ? '?openVoice=true' : ''
    window.location.href = `/projects/${projectId}/project_versions/${projectVersionId}/subtitles/${subtitle.id}/edit${urlParams}`
  }

  const toggleDropdown = () => {
    if (!hasDropdownOptions) { return }

    setShowDropdown(!showDropdown)
  }

  const handleOpenImportSrtPopup = () => {
    props.handleOpenImportSrtPopup({ subtitleId: subtitle.id, projectVersionId })
    setShowDropdown(false)
  }

  const handleOpenExternalTokenPopup = () => {
    props.handleOpenExternalTokenPopup({ subtitleId: subtitle.id })
    setShowDropdown(false)
  }

  const handleDeleteLanguage = () => {
    if (!shouldAllowToDeleteLanguage) { return }

    props.handleDeleteLanguage({ subtitleId: subtitle.id, projectVersionId })
    setShowDropdown(false)
  }

  const renderFlagAndName = () => {
    return (
      <React.Fragment>
        {isCountryFlagAvailable ? (
          <Flag country={countryFlag} size={15}/>
        ) : (
          <WorldFlag className="LanguageRow__language-worldflag"/>
        )}
        <div className="LanguageRow__language-name">
          {language}
        </div>
        {transcription && (
          <div className="LanguageRow__language-source">
            Source
          </div>
        )}
      </React.Fragment>
    )
  }

  const renderStatus = () => {
    return (
      <div className={`LanguageRow__status-badge ${languageStatusClass}`}>
        {languageStatusToRender}
        {isDone && <Done />}
        {isPendingTranscriptOrInsufficientCreditsOrWaitingReview && <Pending />}
        {isInProgress && <InProgress />}
      </div>
    )
  }

  const actionDropdownList = (
    <ul>
      {shouldAllowToShareLanguage && (
        <li
          className="LanguagesTable__action-dropdown-item"
          onClick={handleOpenExternalTokenPopup}
        >
          <div className="LanguagesTable__action-dropdown-item-text">
            Share language
          </div>
        </li>
      )}
      {shouldAllowToImportSrt && (
        <li
          className="LanguagesTable__action-dropdown-item"
          onClick={handleOpenImportSrtPopup}
        >
          <div className="LanguagesTable__action-dropdown-item-text">
            Import SRT
          </div>
        </li>
      )}
      {shouldAllowToDeleteLanguage && (
        <li
          className="LanguagesTable__action-dropdown-item"
          onClick={handleDeleteLanguage}
        >
          <div className="LanguagesTable__action-dropdown-item-text">
            Delete language
          </div>
        </li>
      )}
    </ul>
  )

  const renderActionDropdown = () => {
    return (
      <ActionDropdown
        showDropdown={showDropdown}
        dropdownList={actionDropdownList}
        classNamesForListContainer="LanguagesTable__action-dropdown LanguagesView__element"
        classNameForClickOutsideListener={classNameForClickOutsideListenerMoreDropdown}
        classNameOfScrollContainer="LanguagesView"
        idOfDropdownElement={moreElementId}
        idOfPortalContainer="dashboard"
        dropdownOffsetX={45}
        dropdownOffsetY={0}
        dropdownWidth={209} // same width as LanguagesTable__action-dropdown
        setShowDropdown={setShowDropdown}
      />
    )
  }

  return (
    <tr
      className={classnames(
        { 'LanguageRow__even': index % 2 === 0 },
        { 'LanguageRow__uneven': index % 2 === 1 }
      )}
    >
      <td
        className={classnames(
          'LanguagesTable__language',
          'LanguageRow__language',
          { 'LanguageRow__link': allowLinkToEditor },
        )}
        onClick={openEditor}
      >
        {renderFlagAndName()}
      </td>
      <td
        className={classnames(
          'LanguagesTable__type',
          'LanguageRow__type',
          { 'LanguageRow__link': allowLinkToEditor },
        )}
        onClick={openEditor}
      >
        {languageType}
      </td>
      <td
        className={classnames(
          'LanguagesTable__status',
          'LanguageRow__status',
          { 'LanguageRow__link': allowLinkToEditor },
        )}
        onClick={openEditor}
      >
        {renderStatus()}
      </td>
      <td
        className={classnames(
          'LanguagesTable__reviewed',
          'LanguageRow__reviewed',
          { 'LanguageRow__link': allowLinkToEditor },
        )}
        onClick={openEditor}
      >
        {percentageReviewed}%
      </td>
      <td
        className={classnames(
          'LanguagesTable__more',
          'LanguageRow__more',
          { 'LanguageRow__more-disabled': !hasDropdownOptions}
        )}
      >
        <div
          className={`LanguageRow__more-wrapper ${classNameForClickOutsideListenerMoreDropdown}`}
          id={moreElementId}
        >
          <More
            onClick={toggleDropdown}
          />
          {showDropdown && renderActionDropdown()}
        </div>
      </td>
    </tr>
  )
}

export default LanguageRow
